import type { ComponentType } from 'react'

import styled from 'styled-components'

import incomeIcon from 'app/assets/icons/summary_icons/income.svg'
import { SectionMinorTitle as SectionMinorTitleBase } from 'app/components/display/text/standard_text/standard-body/component'
import { ContainerHeader as ContainerHeaderBase } from 'app/components/display/text/standard_text/standard-headings/component'
import { warn as logWarn } from 'app/services/snugLogger'
import {
  formatCurrency,
  formatISODate,
  formatNextPay,
  formatPercentage,
  getMissingEssentialsLabel,
} from 'app/utils/financial/formatters'

// ContainerHeader is a legacy .jsx component whose TS inference marks props as
// required when they have no default values, though all are truly optional at
// runtime. Cast to an all-optional interface so the call site only needs to
// pass the meaningful props.
const ContainerHeader = ContainerHeaderBase as ComponentType<{
  icon?: string
  text?: string
  textClass?: string
  isEditable?: boolean
  onEditClick?: () => void
  editTitle?: string
  componentClass?: string
  onNewNoteUpdated?: unknown
  newNotesContent?: unknown
  onNoteIconClicked?: unknown
  onNewNoteAdded?: unknown
  onNoteCrossClicked?: unknown
  interpreteNoteResult?: Record<string, unknown>
  displayedApplicant?: Record<string, unknown>
  section?: string
  enableNotesButton?: boolean
  editBtnClass?: string
  toggleRequestInfoModal?: unknown
  isManagerAdded?: boolean
  toggleEditRenterApplication?: unknown
  targetGUID?: unknown
  iconHeaderCmp?: unknown
}>

// SectionMinorTitle is a .jsx component; its TS inference treats `text` as
// required and other props as possibly-required. Cast so we can pass only
// `text` without synthetic undefined assignments.
const SectionMinorTitle = SectionMinorTitleBase as ComponentType<{
  text: string
  componentClass?: string
  textClass?: string
  imgSrc?: string
  imgAlt?: string
}>

// Row label constants — single source of truth for label strings so future
// copy changes apply once rather than hunting across component + tests.
const LABEL_MONTHLY_INCOME = 'Monthly income'
const LABEL_MONTHLY_RENT = 'Monthly rent'
const LABEL_TOTAL_SAVINGS = 'Total savings'
const LABEL_RENT_TO_INCOME = 'Rent-to-income ratio'
const LABEL_VERIFIED_DATE = 'Verified date'
const LABEL_WEEKLY_SURPLUS = 'Weekly surplus after rent & commitments'
const LABEL_INCOME_STABILITY = 'Income stability'
const LABEL_INCOME_CONFIDENCE = 'Income confidence'
const LABEL_NEXT_PAY = 'Next pay'
const LABEL_MISSING_ESSENTIALS = 'Missing essentials'

// Bank-verified palette: matches comparison-indicator.match in
// manager_income_financials_container/style.scss. No shared SCSS variable
// exists for these greens — update both sites if the design token changes.
const BANK_VERIFIED_BG = '#e8f5e9'
const BANK_VERIFIED_FG = '#2e7d32'

const BankVerifiedBadge = styled.div.attrs({
  className: 'bank-verified-badge',
})`
  display: inline-flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 4px 10px;
  border-radius: 4px;
  background: ${BANK_VERIFIED_BG};
  color: ${BANK_VERIFIED_FG};
`
BankVerifiedBadge.displayName = 'BankVerifiedBadge'

const BankVerifiedPrimary = styled.span`
  font-size: 13px;
  font-weight: 600;
`
BankVerifiedPrimary.displayName = 'BankVerifiedPrimary'

const BankVerifiedSecondary = styled.span`
  font-size: 12px;
  font-weight: 400;
  opacity: 0.85;
`
BankVerifiedSecondary.displayName = 'BankVerifiedSecondary'

const MetricRow = styled.div.attrs({ className: 'metric-row' })`
  margin-bottom: 5px;
`
MetricRow.displayName = 'MetricRow'

const MetricValue = styled.span.attrs({ className: 'metric-value' })`
  font-size: 14px;
`
MetricValue.displayName = 'MetricValue'

interface RenterVerifiedIncome {
  isRedacted?: boolean
  avgMonthlyIncome?: number | null
  avgMonthlyRent?: number | null
  totalSavings?: number | null
  /**
   * 0–1 fraction, e.g. 0.28 = 28%.
   * Displayed as `Math.round(value * 100)%` — if the API ever returns 0–100
   * instead, the display would be 100× wrong.
   */
  rentToIncomeRatio?: number | null
  reportCreatedAt?: string | null
  weeklySurplusAfterRentAndFinances?: number | null
  incomeStabilityDuration?: string | null
  incomeConfidence?: string | null
  nextPayDate?: string | null
  nextPayAmount?: number | null
  missingEssentialsGroceries?: boolean | null
  missingEssentialsTransport?: boolean | null
  missingEssentialsAccommodation?: boolean | null
}

interface Props {
  verifiedIncome?: RenterVerifiedIncome | null
  attachedToProfile: boolean
}

interface VerifiedMetricsProps {
  verifiedIncome: RenterVerifiedIncome
}

function VerifiedMetrics({ verifiedIncome }: VerifiedMetricsProps) {
  const formattedDate = formatISODate(verifiedIncome.reportCreatedAt)
  const nextPay = formatNextPay(
    verifiedIncome.nextPayDate,
    verifiedIncome.nextPayAmount,
  )
  const missingEssentials = getMissingEssentialsLabel(
    verifiedIncome.missingEssentialsGroceries,
    verifiedIncome.missingEssentialsTransport,
    verifiedIncome.missingEssentialsAccommodation,
  )

  return (
    <div className="application-income-container">
      <MetricRow>
        <SectionMinorTitle text={LABEL_MONTHLY_INCOME} />
        <MetricValue>
          {formatCurrency(verifiedIncome.avgMonthlyIncome)}
        </MetricValue>
      </MetricRow>
      {verifiedIncome.avgMonthlyRent != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_MONTHLY_RENT} />
          <MetricValue>
            {formatCurrency(verifiedIncome.avgMonthlyRent)}
          </MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.totalSavings != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_TOTAL_SAVINGS} />
          <MetricValue>
            {formatCurrency(verifiedIncome.totalSavings)}
          </MetricValue>
        </MetricRow>
      )}
      <MetricRow>
        <SectionMinorTitle text={LABEL_RENT_TO_INCOME} />
        <MetricValue>
          {formatPercentage(verifiedIncome.rentToIncomeRatio)}
        </MetricValue>
      </MetricRow>
      {formattedDate && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_VERIFIED_DATE} />
          <MetricValue>{formattedDate}</MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.weeklySurplusAfterRentAndFinances != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_WEEKLY_SURPLUS} />
          <MetricValue>
            {formatCurrency(verifiedIncome.weeklySurplusAfterRentAndFinances)}
            /wk
          </MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.incomeStabilityDuration != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_INCOME_STABILITY} />
          <MetricValue>{verifiedIncome.incomeStabilityDuration}</MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.incomeConfidence != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_INCOME_CONFIDENCE} />
          <MetricValue>{verifiedIncome.incomeConfidence}</MetricValue>
        </MetricRow>
      )}
      {nextPay != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_NEXT_PAY} />
          <MetricValue>{nextPay}</MetricValue>
        </MetricRow>
      )}
      <MetricRow>
        <SectionMinorTitle text={LABEL_MISSING_ESSENTIALS} />
        <MetricValue>{missingEssentials}</MetricValue>
      </MetricRow>
    </div>
  )
}

const RenterVerifiedFinancialsContainer = ({
  verifiedIncome,
  attachedToProfile,
}: Props) => {
  if (verifiedIncome?.isRedacted) {
    logWarn(
      '[RenterVerifiedFinancials] isRedacted unexpectedly true on renter view — backend should not redact own data',
    )
    return null
  }
  if (!verifiedIncome) return null
  if (!attachedToProfile) return null

  return (
    <div className="container-component renter-verified-financials-container bbs">
      <ContainerHeader
        icon={incomeIcon}
        text="Verified Financial Insights"
        isEditable={false}
        interpreteNoteResult={{}}
        section="verifiedFinancials"
      />
      <div className="application-container-padding">
        <BankVerifiedBadge aria-label="Bank Verified via Open Banking (CDR)">
          <BankVerifiedPrimary>Bank Verified</BankVerifiedPrimary>
          <BankVerifiedSecondary>via Open Banking (CDR)</BankVerifiedSecondary>
        </BankVerifiedBadge>
        <VerifiedMetrics verifiedIncome={verifiedIncome} />
      </div>
    </div>
  )
}

export default RenterVerifiedFinancialsContainer
